/**
 * Users Service
 * Handles all user management API calls
 */

import { apiService, ApiResponse } from './api.service';
import { API_URL } from '../config/api.config';

/** User Role Enum */
export type UserRole = 'ADMIN' | 'MANAGER' | 'CHEF' | 'STAFF' | 'VIEWER';

/** User Interface — matches backend Prisma select */
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  allowedCustomerTypeIds?: string[];
  isActive: boolean;
  tenantId: string;
  branchId?: string;
  phone?: string;
  avatar?: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  tenant?: { id: string; name: string };
  branch?: { id: string; name: string };
}

/** Create User DTO */
export interface CreateUserDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  allowedCustomerTypeIds?: string[];
  tenantId: string;
  branchId?: string;
  phone?: string;
  avatar?: string;
  isActive?: boolean;
}

/** Update User DTO */
export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  allowedCustomerTypeIds?: string[];
  role?: UserRole;
  phone?: string;
  branchId?: string;
  isActive?: boolean;
  avatar?: string;
}

/** Change Password DTO */
export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/** Users Query Parameters */
export interface UsersQuery {
  page?: number;
  limit?: number;
  role?: UserRole;
  branchId?: string;
  tenantId?: string;
  isActive?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/** Raw backend paginated response shape */
export interface PaginatedUsers {
  data: User[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/** User Statistics */
export interface UserStats {
  total: number;
  active: number;
  inactive: number;
  byRole: {
    ADMIN: number;
    MANAGER: number;
    STAFF: number;
    VIEWER: number;
  };
}

class UsersService {
  /** Get all users — returns the raw paginated shape */
  async getUsers(query?: UsersQuery): Promise<ApiResponse<PaginatedUsers>> {
    const params = new URLSearchParams();
    if (query?.page)    params.append('page',    query.page.toString());
    if (query?.limit)   params.append('limit',   query.limit.toString());
    if (query?.role)    params.append('role',    query.role);
    if (query?.branchId) params.append('branchId', query.branchId);
    if (query?.tenantId) params.append('tenantId', query.tenantId);
    if (query?.search)  params.append('search',  query.search);
    if (query?.sortBy)  params.append('sortBy',  query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
    if (query?.isActive !== undefined) params.append('isActive', query.isActive.toString());

    const qs = params.toString() ? `?${params.toString()}` : '';

    // Use a raw fetch so we preserve the { data, meta } envelope instead of
    // having apiService unwrap only data.data
    const token = localStorage.getItem('accessToken');
    const activeTenantId = localStorage.getItem('activeTenantId');
    const currentBranchRaw = localStorage.getItem('currentBranch');
    let activeBranchId: string | null = null;

    if (currentBranchRaw) {
      try {
        const currentBranch = JSON.parse(currentBranchRaw);
        if (currentBranch?.id) {
          activeBranchId = currentBranch.id;
        }
      } catch {
        // Ignore malformed cached branch JSON.
      }
    }

    const res = await fetch(`${API_URL}/users${qs}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(activeTenantId ? { 'X-Tenant-Id': activeTenantId } : {}),
        ...(activeBranchId ? { 'X-Branch-Id': activeBranchId } : {}),
      },
    });

    if (!res.ok) {
      throw { message: `Failed to fetch users: ${res.status}`, status: res.status };
    }

    const json = await res.json();
    // Backend returns { data: [...], meta: { total, page, limit, totalPages } }
    return { success: true, data: json };
  }

  /** Get single user by ID */
  async getUserById(id: string): Promise<ApiResponse<User>> {
    return apiService.get<User>(`/users/${id}`);
  }

  /** Create new user */
  async createUser(data: CreateUserDto): Promise<ApiResponse<User>> {
    return apiService.post<User>('/users', data);
  }

  /** Update user */
  async updateUser(id: string, data: UpdateUserDto): Promise<ApiResponse<User>> {
    return apiService.patch<User>(`/users/${id}`, data);
  }

  /** Delete user */
  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/users/${id}`);
  }

  /** Toggle active status — backend endpoint: PATCH /users/:id/toggle-active */
  async toggleUserStatus(id: string): Promise<ApiResponse<User>> {
    return apiService.patch<User>(`/users/${id}/toggle-active`);
  }

  /** Change password */
  async changePassword(data: ChangePasswordDto): Promise<ApiResponse<void>> {
    return apiService.post<void>('/auth/change-password', data);
  }
}

export const usersService = new UsersService();